import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import { FormInput, FormSelect } from '../../components/finance';
import { Plus, Pencil, Trash, Users, ArrowLeft } from '@phosphor-icons/react';
import RouteForm from '../../components/transport/RouteForm';
import DialogModal from '../../components/DialogModal';
import './transport.css';

function buildDeleteImpactMessage(impact, noneMessage) {
  let message;
  if (impact.total_to_be_deleted > 0) {
    const deletedLines = Object.entries(impact.will_be_deleted)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message = `This will permanently delete ${impact.total_to_be_deleted} record(s):\n${deletedLines}\n\nThis cannot be undone.`;
  } else {
    message = noneMessage;
  }
  if (Object.keys(impact.will_be_preserved || {}).length > 0) {
    const preservedLines = Object.entries(impact.will_be_preserved)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message += `\n\nThese will be kept, just unlinked:\n${preservedLines}`;
  }
  return message;
}

const ROUTE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' }
];

function RoutesManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [filters, setFilters] = useState({ status: '' });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, routeId: null, message: '', loading: false });

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        setLoading(true);
        const params = { ...filters, ...pagination };
        const data = await transportService.listRoutes(params);
        setRoutes(Array.isArray(data) ? data : data.routes || []);
      } catch (error) {
        toast.error('Failed to load routes: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [filters, pagination]);

  const handleCreate = async (formData) => {
    try {
      await transportService.createRoute(formData);
      toast.success('Route created successfully');
      setShowModal(false);
      setSelectedRoute(null);
      const data = await transportService.listRoutes(pagination);
      setRoutes(Array.isArray(data) ? data : data.routes || []);
    } catch (error) {
      toast.error('Failed to create route: ' + error.message);
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await transportService.updateRoute(id, formData);
      toast.success('Route updated successfully');
      setShowModal(false);
      setSelectedRoute(null);
      const data = await transportService.listRoutes(pagination);
      setRoutes(Array.isArray(data) ? data : data.routes || []);
    } catch (error) {
      toast.error('Failed to update route: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    setDeleteDialog({ isOpen: true, routeId: id, message: 'Checking what this will affect...', loading: true });
    try {
      const impact = await transportService.getRouteDeleteImpact(id);
      const message = buildDeleteImpactMessage(impact, 'No enrollments, attendance, or other records are tied to this route yet — safe to delete.');
      setDeleteDialog({ isOpen: true, routeId: id, message, loading: false });
    } catch (error) {
      console.error('Error fetching delete impact:', error);
      setDeleteDialog({ isOpen: true, routeId: id, message: 'Delete this route? This cannot be undone.', loading: false });
    }
  };

  const confirmDeleteRoute = async () => {
    const id = deleteDialog.routeId;
    setDeleteDialog({ isOpen: false, routeId: null, message: '', loading: false });
    try {
      await transportService.deleteRoute(id);
      toast.success('Route deleted successfully');
      const data = await transportService.listRoutes(pagination);
      setRoutes(Array.isArray(data) ? data : data.routes || []);
    } catch (error) {
      toast.error('Failed to delete route: ' + error.message);
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading routes...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page routes-management">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Routes Management</h1>
              <p>Create and manage transport routes</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => { setSelectedRoute(null); setShowModal(true); }}>
            <Plus size={18} /> Add Route
          </button>
        </div>

        <div className="filters-section">
          <FormSelect
            label="Status"
            value={filters.status}
            options={ROUTE_STATUS_OPTIONS}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPagination({ ...pagination, skip: 0 }); }}
            placeholder="All Status"
          />
        </div>

        <div className="table-section">
          {routes.length === 0 ? (
            <div className="empty-state">
              <p>No routes found. Create your first route to get started.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Route Code</th>
                  <th>Route Name</th>
                  <th>Start → End</th>
                  <th>Distance</th>
                  <th>Duration</th>
                  <th>Pickup Time</th>
                  <th>Monthly Fee</th>
                  <th>Students</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {routes.map(route => (
                  <tr key={route.id}>
                    <td><strong>{route.route_code}</strong></td>
                    <td>{route.route_name}</td>
                    <td>{route.start_point} → {route.end_point}</td>
                    <td>{route.distance_km} km</td>
                    <td>{route.estimated_duration_minutes} min</td>
                    <td>{route.pickup_time}</td>
                    <td><strong>GHS {(route.fee_amount || 0).toFixed(2)}</strong></td>
                    <td>{route.student_count || 0}</td>
                    <td><span className={`badge badge-${route.status}`}>{route.status}</span></td>
                    <td className="actions">
                      <button className="btn-icon" onClick={() => { setSelectedRoute(route); setShowModal(true); }} title="Edit">
                        <Pencil size={18} />
                      </button>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(route.id)} title="Delete">
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button disabled={pagination.skip === 0} onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}>
            Previous
          </button>
          <span>Page {Math.floor(pagination.skip / pagination.limit) + 1}</span>
          <button disabled={routes.length < pagination.limit} onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}>
            Next
          </button>
        </div>

        {showModal && (
          <RouteForm
            route={selectedRoute}
            onSave={selectedRoute ? (id, data) => handleUpdate(id, data) : (_, data) => handleCreate(data)}
            onClose={() => { setShowModal(false); setSelectedRoute(null); }}
          />
        )}
      </div>

      <DialogModal
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, routeId: null, message: '', loading: false })}
        title="Delete Route"
        message={deleteDialog.message}
        type="warning"
        isLoading={deleteDialog.loading}
        disableCloseWhenLoading={false}
        primaryAction={deleteDialog.loading ? null : { label: 'Delete', onClick: confirmDeleteRoute }}
        secondaryAction={deleteDialog.loading ? null : { label: 'Cancel', onClick: () => {} }}
      />
    </Layout>
  );
}

export default RoutesManagementPage;
